/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/**
 * @typedef {'facebook'|'twitter'|'linkedin'|'xing'|'email'|'whatsapp'|'reddit'} SocialService
 */

/**
 * @typedef {Object} SocialConfig
 * @property {boolean} [facebook]
 * @property {boolean} [twitter]
 * @property {boolean} [linkedin]
 * @property {boolean} [xing]
 * @property {boolean} [email]
 * @property {boolean} [whatsapp]
 * @property {boolean} [reddit]
 */

/**
 * Social media sharing helper.
 * @constructor
 * @param {SocialConfig} config - Map of service name → enabled flag.
 */
export const Social = function (config) {
    const self = this;
    const services = [
        'facebook',
        'twitter',
        'linkedin',
        'xing',
        'email',
        'whatsapp',
        'reddit'
    ];

    this.config = config;
    this.services = services;

    /**
     * Returns `true` if at least one service is enabled.
     * @returns {boolean}
     */
    this.isEnabled = function () {
        const values = this.services.map((service) => self.config[service]);
        return values.some((enabled) => enabled === true);
    };

    /**
     * Returns the list of enabled service identifiers.
     * @returns {SocialService[]}
     */
    this.enabledServices = function () {
        return this.services.filter((service) => self.config[service] === true);
    };

    /**
     * Builds the share URL for a given social service.
     * @param {SocialService} service - Service identifier.
     * @param {string} url - The URL to share.
     * @param {string} title - Page or node title (truncated to 60 chars).
     * @param {string} [text] - Optional body text (truncated to 230 chars).
     * @returns {string|undefined} A fully-encoded share URL, or `undefined` for unknown services.
     */
    this.getServiceLinkUrl = (service, url, title, text) => {
        const truncate = (str, n) => {
            if (!str || str.length <= n) {
                return str || '';
            }
            const subString = str.substring(0, n - 1);
            return `${subString.substring(0, subString.lastIndexOf(' '))}…`;
        };
        const truncatedTitle = truncate(title, 60);
        const truncatedText = truncate(text, 230);
        const encodedUrl = encodeURIComponent(url);
        const encodedTitle = encodeURIComponent(truncatedTitle);
        // Each service that shares body text builds its own wording from
        // the title, the text and the URL, so this is set per branch.
        let encodedText;
        switch (service) {
            case 'facebook':
                // https://developers.facebook.com/docs/plugins/share-button
                // https://webmasters.stackexchange.com/questions/13657/share-buttons-vs-sharer-urls
                return `https://www.facebook.com/sharer.php?u=${encodedUrl}`;
            case 'twitter':
                // https://developer.twitter.com/en/docs/twitter-for-websites/tweet-button/overview
                if (truncatedText) {
                    encodedText = encodeURIComponent(
                        `${truncatedTitle}\n${truncatedText}`
                    );
                    return `https://x.com/intent/post?url=${encodedUrl}&text=${encodedText}`;
                } else {
                    return `https://x.com/intent/post?url=${encodedUrl}&text=${encodedTitle}`;
                }
            case 'linkedin':
                // https://docs.microsoft.com/en-us/linkedin/consumer/integrations/self-serve/plugins/share-plugin
                // https://stackoverflow.com/questions/10713542/how-to-make-a-custom-linkedin-share-button
                return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
            case 'xing':
                // https://dev.xing.com/plugins/share_button/docs
                return `https://www.xing.com/spi/shares/new?url=${encodedUrl}`;
            case 'email':
                if (truncatedText) {
                    encodedText = encodeURIComponent(
                        `${truncatedText}\n\n${url}`
                    );
                    return `mailto:?subject=${encodedTitle}&body=${encodedText}`;
                } else {
                    return `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;
                }
            case 'reddit':
                // TODO: Source?
                return `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
            case 'whatsapp':
                // https://faq.whatsapp.com/general/chats/how-to-use-click-to-chat/
                if (truncatedText) {
                    encodedText = encodeURIComponent(
                        `${truncatedTitle}\n${truncatedText}\n\n${url}`
                    );
                } else {
                    encodedText = encodeURIComponent(
                        `${truncatedTitle}\n${url}`
                    );
                }
                return `https://wa.me/?text=${encodedText}`;
        }
    };

    /**
     * Returns a FontAwesome `<i>` tag for the given service.
     * @param {SocialService} service
     * @returns {string} HTML string.
     */
    this.getServiceIconTag = (service) => {
        const icon_class = {
            facebook: 'fab fa-facebook',
            twitter: 'fab fa-x-twitter',
            linkedin: 'fab fa-linkedin',
            xing: 'fab fa-xing',
            email: 'fa fa-envelope',
            reddit: 'fab fa-reddit-alien',
            whatsapp: 'fab fa-whatsapp'
        };
        return `<i class="${icon_class[service]}"></i>`;
    };

    /**
     * Returns the human-readable display name for a service.
     * @param {SocialService} service
     * @returns {string|undefined} Display name, or `undefined` for unknown services.
     */
    this.getDisplayName = (service) => {
        const servicesByName = {
            facebook: 'Facebook',
            twitter: 'X',
            linkedin: 'LinkedIn',
            xing: 'Xing',
            email: 'E-Mail',
            reddit: 'Reddit',
            whatsapp: 'WhatsApp'
        };
        return servicesByName[service];
    };
};

export default Social;
